import { useMutation } from '@tanstack/react-query';

import { API_END_POINT, AUTH_GRAVITEE_ENABLED, HOST_MB } from '@/lib/constants';
import { apiRequest } from '@/lib/api/client';
import { passUrl } from '@/lib/api/pass-origin';
import { clearSession, getSession, setSession, type Provider } from '@/lib/storage';
import { useUiStore } from '@/stores/ui-store';
import { getGraviteeLogoutUrl, logoutGraviteeTokens } from '@/lib/auth/gravitee';

interface LoginResponse {
    /** Renseigné quand le client doit être renvoyé vers l'espace de sa marque blanche. */
    redirect_to?: string;
    message_footer?: string | null;
    message_warning?: string | null;
    img_footer?: string | null;
    user: { whitelabel?: number | string };
}

export interface LoginValues {
    username: string;
    password: string;
}

const postLogin = ({ username, password }: LoginValues, provider: Provider) =>
    apiRequest<LoginResponse>({
        url: API_END_POINT,
        method: 'POST',
        // Le backend gère les valeurs par défaut pour client_id, grant_type
        data: { username, password, owner_belair_id: HOST_MB, provider, scope: '*' },
    });

/**
 * Connexion classique (hors MFA).
 *
 * Si la tentative en `customers` échoue, on retente en `pros` avant d'abandonner : les comptes
 * professionnels COOLTRA se connectent à l'espace assuré (cf. `useUser`). Le jeton est posé en
 * cookie HttpOnly par le Pass ; le localStorage ne garde que le drapeau de session.
 */
export const useLogin = () =>
    useMutation({
        mutationFn: async (values: LoginValues) => {
            let provider: Provider = 'customers';
            let data: LoginResponse;

            try {
                data = await postLogin(values, provider);
            } catch {
                provider = 'pros';
                data = await postLogin(values, provider);
            }

            return { data, provider };
        },
        onSuccess: ({ data, provider }) => {
            // Redirection vers l'espace whitelabel (ex : AM Gestion) : le Pass ne renvoie
            // redirect_to que si le client n'est pas déjà sur le bon espace.
            if (data.redirect_to) {
                window.location.href = data.redirect_to;
                return;
            }

            const { message_footer, img_footer, message_warning } = data;

            setSession({
                authenticated: true,
                welcome: { message_footer, message_warning, img_footer },
                id_whitelabel: data.user.whitelabel,
                provider,
            });
            useUiStore.getState().setLogin(true);
        },
    });

/**
 * Déconnexion. Redirige elle-même vers la page de connexion : l'appelant n'a pas à naviguer.
 *
 * En MFA (ou après une redirection cross-brand), on ferme la session SSO du tenant Gravitee
 * concerné ; sinon on vide la session et on appelle la déconnexion du Pass.
 */
export const logout = (): void => {
    const session = getSession() ?? {};
    const idToken = session.id_token;
    // Cross-brand : si l'utilisateur est arrivé ici via un redirect depuis un autre tenant
    // Gravitee (OTT), c'est le tenant source qu'il faut invalider.
    const graviteeLogout = session.gravitee_logout;

    if (AUTH_GRAVITEE_ENABLED || graviteeLogout) {
        // Forcer la re-saisie au prochain login
        sessionStorage.setItem('gravitee_force_login', 'true');
        // Bascule login=false tout de suite : sinon l'écran de connexion, monté par la
        // navigation vers /login, verrait une session ouverte et renverrait vers « / » avant
        // que la déconnexion Gravitee n'aboutisse. Le localStorage reste intact : on y lit
        // gravitee_logout ci-dessous.
        useUiStore.getState().setLogin(false);

        void logoutGraviteeTokens().finally(() => {
            let url: string;
            if (graviteeLogout) {
                const params = new URLSearchParams({
                    client_id: graviteeLogout.client_id,
                    target_url: window.location.origin + '/login',
                    id_token_hint: graviteeLogout.id_token,
                    invalidate_tokens: 'true',
                });
                url = `${graviteeLogout.logout_url}?${params.toString()}`;
            } else {
                url = getGraviteeLogoutUrl(window.location.origin + '/login', idToken);
            }
            window.location.href = url;
        });
        return;
    }

    // Mode classique : on vide la session, on prévient le Pass, puis on recharge sur /login
    // pour repartir d'un cache vide — l'ancien parcours aboutissait au même rechargement.
    clearSession();
    useUiStore.getState().setLogin(false);
    void apiRequest({ url: passUrl('/auth/logout'), method: 'POST' })
        .catch(() => {})
        .finally(() => {
            window.location.href = '/login';
        });
};
